import React, { useState, useEffect } from 'react';
import CustomDialog from './CustomDialog';
import { API, authHeaders } from '../services/apiConfig';
import './Cart.css';
import { FaRupeeSign, FaTrash, FaShoppingCart, FaArrowRight, FaTimes, FaPlus, FaMinus } from 'react-icons/fa';

const resolveImg = (url) => {
  if (!url) return '/images/product.jpg';
  return url.startsWith('http') ? url : `${API}${url}`;
};

const fmtAmt = (n) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Cart({ onClose }) {
  const [items, setItems]   = useState([]);
  const [summary, setSummary] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [removing, setRemoving] = useState(null);

  useEffect(() => { fetchCart(); }, []);

  const fetchCart = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) return;
    try {
      const [itemsRes, sumRes] = await Promise.all([
        fetch(`${API}/api/cart/user/${user.userId}`, { headers: authHeaders() }),
        fetch(`${API}/api/cart/user/${user.userId}/summary`, { headers: authHeaders() })
      ]);
      setItems(await itemsRes.json());
      setSummary(await sumRes.json());
    } catch (e) { console.error(e); }
  };

  const updateQty = async (id, qty) => {
    if (qty < 1) { removeItem(id); return; }
    await fetch(`${API}/api/cart/${id}?quantity=${qty}`, { method: 'PUT', headers: authHeaders() });
    fetchCart();
  };

  const removeItem = async (id) => {
    setRemoving(id);
    try {
      await fetch(`${API}/api/cart/${id}`, { method: 'DELETE', headers: authHeaders() });
      fetchCart();
    } catch { setDialog({ message: 'Failed to remove item', type: 'error', onClose: () => setDialog(null) }); }
    finally { setRemoving(null); }
  };

  const clearCart = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    try {
      await fetch(`${API}/api/cart/user/${user.userId}`, { method: 'DELETE', headers: authHeaders() });
      fetchCart();
    } catch { setDialog({ message: 'Failed to clear cart', type: 'error', onClose: () => setDialog(null) }); }
  };

  return (
    <>
      <div className="ct-overlay" onClick={onClose}>
        <div className="ct-drawer" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="ct-hdr">
            <div className="ct-hdr-left">
              <div className="ct-hdr-icon"><FaShoppingCart /></div>
              <div>
                <h2>Shopping Cart</h2>
                <span>{items.length} item{items.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <button className="ct-close" onClick={onClose}><FaTimes /></button>
          </div>

          {items.length === 0 ? (
            /* Empty state */
            <div className="ct-empty">
              <div className="ct-empty-bag"><FaShoppingCart /></div>
              <h3>Your cart is empty</h3>
              <p>Add some products to get started</p>
              <button className="ct-shop-btn" onClick={() => {
                onClose();
                setTimeout(() => {
                  document.getElementById('standard')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 150);
              }}>
                Continue Shopping <FaArrowRight />
              </button>
            </div>
          ) : (
            <>
              {/* Items */}
              <div className="ct-items">
                {items.map(item => (
                  <div key={item.cartId} className={`ct-item ${removing === item.cartId ? 'removing' : ''}`}>
                    <div className="ct-item-img">
                      <img
                        src={resolveImg(item.image)}
                        alt={item.productName}
                        onError={e => { e.target.src = '/images/product.jpg'; }}
                      />
                    </div>

                    <div className="ct-item-body">
                      <div className="ct-item-top">
                        <div>
                          <p className="ct-item-name">{item.productName}</p>
                          {(item.variantColor || item.variantSize) && (
                            <p className="ct-item-variant">
                              {item.variantColor && <span>● {item.variantColor}</span>}
                              {item.variantSize  && <span>  {item.variantSize}</span>}
                            </p>
                          )}
                          <p className="ct-item-unit">
                            <FaRupeeSign />{fmtAmt(item.price)} / piece
                          </p>
                        </div>
                        <button
                          className="ct-remove"
                          onClick={() => removeItem(item.cartId)}
                          title="Remove"
                        >
                          <FaTrash />
                        </button>
                      </div>

                      <div className="ct-item-bottom">
                        <div className="ct-qty">
                          <button onClick={() => updateQty(item.cartId, item.cartQuantity - 1)}><FaMinus /></button>
                          <span>{item.cartQuantity}</span>
                          <button onClick={() => updateQty(item.cartId, item.cartQuantity + 1)}><FaPlus /></button>
                        </div>
                        <p className="ct-item-total">
                          <FaRupeeSign />{fmtAmt(item.price * item.cartQuantity)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              {summary && (() => {
                const shipping = summary.subtotal * 0.08;
                const grandTotal = summary.subtotal + summary.tax + shipping;
                return (
                <div className="ct-summary">
                  <div className="ct-summary-rows">
                    <div className="ct-sum-row">
                      <span>Subtotal ({summary.totalItems} items)</span>
                      <span><FaRupeeSign />{fmtAmt(summary.subtotal)}</span>
                    </div>
                    <div className="ct-sum-row">
                      <span>GST (18%)</span>
                      <span><FaRupeeSign />{fmtAmt(summary.tax)}</span>
                    </div>
                    <div className="ct-sum-row">
                      <span>Shipping (8%)</span>
                      <span><FaRupeeSign />{fmtAmt(shipping)}</span>
                    </div>
                    <div className="ct-sum-row ct-sum-total">
                      <span>Total Payable</span>
                      <span><FaRupeeSign />{fmtAmt(grandTotal)}</span>
                    </div>
                  </div>

                  <div className="ct-actions">
                    <button className="ct-clear-btn" onClick={clearCart}>
                      <FaTrash /> Clear All
                    </button>
                    <button
                      className="ct-checkout-btn"
                      onClick={() => {
                        const user = JSON.parse(localStorage.getItem('user'));
                        const checkoutBase = window.location.hostname === 'localhost' ? 'http://localhost:8083' :
                          
                          `https://corgifonline.com`;
                        window.location.href = `${checkoutBase}/checkout?userId=${user?.userId}`;
                      }}
                    >
                      Checkout <FaArrowRight />
                    </button>
                  </div>
                </div>
                );
              })()}
            </>
          )}
        </div>
      </div>
      {dialog && <CustomDialog message={dialog.message} type={dialog.type} onClose={dialog.onClose} />}
    </>
  );
}
